import * as React from 'react'

import { observer } from 'mobx-react'
import { Marker, MarkerDragEvent } from 'react-map-gl/mapbox'

import DomMarkerCircle from '~/client/src/shared/components/SitemapHelpers/components/DomMarkerCircle'
import { getPictogramGlyph } from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import { PICTOGRAM_MARKER_RADIUS } from '~/client/src/shared/map/markerGeometry'

import MapViewItemsSetupStore from '../../stores/MapViewItemsSetup.store'
import PendingPlacementToolbar from './PendingPlacementToolbar'

// The ring's inset from the marker (tw:p-[2px]), so a square pictogram's ring stays concentric with it.
const RING_GAP_PX = 2

/**
 * Keeps the dashed pending pin above every regular marker. Mapbox sorts
 * markers by screen y-position for perspective, which makes co-located
 * markers (e.g. pending pin seeded on top of its parent Location) stack
 * unpredictably — the parent ends up catching drag/click events. An
 * explicit z-index bypasses that sort; value is intentionally far above
 * any other marker class in the app (the highest we use is `z-index-1`
 * in `GlobeViewMarker`).
 */
const PENDING_PLACEMENT_Z_INDEX = 1000

interface IProps {
  store: MapViewItemsSetupStore
}

@observer
export default class GlobeViewPendingPlacement extends React.Component<IProps> {
  public render() {
    const { pendingPlacement, mapViewItems } = this.props.store
    const id = pendingPlacement.id
    const coord = pendingPlacement.coord
    if (!id || !coord) {
      return null
    }
    const item = mapViewItems.find(i => i.id === id)
    if (!item) {
      return null
    }

    const isConfirming = pendingPlacement.isConfirming

    return (
      <Marker
        longitude={coord.longitude}
        latitude={coord.latitude}
        draggable={!isConfirming}
        onDragEnd={this.onDragEnd}
        style={{ zIndex: PENDING_PLACEMENT_Z_INDEX }}
      >
        <div className="tw:relative">
          <div className="tw:relative tw:flex tw:items-center tw:justify-center tw:p-[2px] tw:opacity-60">
            <div
              className={`tw:absolute tw:inset-0 tw:rounded-full tw:border-2 tw:border-dashed ${
                isConfirming ? 'tw:animate-[spin_2.5s_linear_infinite]' : ''
              }`}
              style={
                getPictogramGlyph(item.iconName)
                  ? { borderRadius: PICTOGRAM_MARKER_RADIUS + RING_GAP_PX }
                  : undefined
              }
            />
            <DomMarkerCircle iconName={item.iconName} color={item.color} />
          </div>
          {!isConfirming && (
            <div className="tw:absolute tw:left-1/2 tw:top-full tw:-translate-x-1/2">
              <PendingPlacementToolbar
                name={item.displayName}
                currentIndex={pendingPlacement.currentIndex}
                total={pendingPlacement.total}
                onPlace={this.onConfirm}
                onCancel={this.onCancel}
              />
            </div>
          )}
        </div>
      </Marker>
    )
  }

  public componentDidMount(): void {
    this.advanceIfHeadMissing()
  }

  public componentDidUpdate(): void {
    this.advanceIfHeadMissing()
  }

  public componentWillUnmount(): void {
    this.props.store.abortPendingPlacement()
  }

  private advanceIfHeadMissing(): void {
    const { pendingPlacement, mapViewItems } = this.props.store
    const id = pendingPlacement.id
    if (!id) {
      return
    }
    const hasItem = mapViewItems.some(i => i.id === id)
    if (!hasItem) {
      this.props.store.cancelPendingPlacement()
    }
  }

  private onDragEnd = (e: MarkerDragEvent): void => {
    this.props.store.updatePendingPlacementCoord({
      latitude: e.lngLat.lat,
      longitude: e.lngLat.lng,
    })
  }

  private onConfirm = (e: React.MouseEvent): void => {
    e.stopPropagation()
    this.props.store.confirmPendingPlacement()
  }

  private onCancel = (e: React.MouseEvent): void => {
    e.stopPropagation()
    this.props.store.cancelPendingPlacement()
  }
}
